/**
 * cusa-midnight-page-reload.js
 *
 * Reusable "reload this page at midnight Pacific" behavior, loaded by the
 * `cusa-midnight-page-reload` section. Built for the CTG homepage — whose ad
 * content is resolved server-side in Liquid using the shop timezone (Pacific)
 * and therefore goes stale the moment the Pacific calendar date rolls over — but
 * it is page-agnostic: drop the section on any template whose server-rendered,
 * date-scheduled content should refresh at the day boundary.
 *
 * Behavior:
 * - On page load, schedules a reload for the next midnight Pacific, with a
 *   24-hour fallback if the midnight calculation ever fails.
 * - When the trigger fires, a warning toast appears with a live 60-second
 *   countdown and a "Refresh now" button. When it reaches zero (or the user
 *   refreshes manually), the page reloads and the server re-renders fresh.
 *
 * Scope:
 * - Runs wherever the section is placed — there is no hardcoded template gate.
 * - No persistence: the timer lives only while this page is open. Navigating
 *   away ends it; the next load starts fresh.
 *
 * Robustness:
 * - The trigger and countdown compare against absolute timestamps rather than
 *   counting elapsed ticks, so they survive background-tab interval throttling
 *   and device sleep — on the next tick, the comparison simply catches up.
 *
 * Testing:
 * - `window.CusaMidnightPageReload.startCountdown()` forces the countdown
 *   immediately so QA doesn't have to wait for midnight.
 * - `window.CusaMidnightPageReload.getState()` returns the computed timestamps.
 */
(function () {
	'use strict';

	// Guard against double-initialization if the section is placed more than once.
	if (window.CusaMidnightPageReload) return;

	const TZ = 'America/Los_Angeles';
	const DAY_MS = 24 * 60 * 60 * 1000;
	const COUNTDOWN_SECONDS = 60;
	const CHECK_INTERVAL_MS = 1000;

	/**
	 * Offset (in ms) between Pacific wall-clock time and UTC at a given instant.
	 * Defined so that: epochUTC = (wall-clock parts interpreted as UTC) - offset.
	 * Uses Intl so DST (PST/PDT) is handled automatically.
	 *
	 * @param {number} ms - Epoch milliseconds to evaluate the offset at.
	 * @returns {number} Offset in milliseconds.
	 */
	function pacificOffsetMs(ms) {
		const dtf = new Intl.DateTimeFormat('en-US', {
			timeZone: TZ,
			hour12: false,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit'
		});
		const parts = dtf.formatToParts(new Date(ms)).reduce((acc, p) => {
			acc[p.type] = p.value;
			return acc;
		}, {});
		// Some engines render midnight as hour "24"; normalize to 0.
		let hour = parseInt(parts.hour, 10);
		if (hour === 24) hour = 0;
		const wallAsUTC = Date.UTC(
			Number(parts.year),
			Number(parts.month) - 1,
			Number(parts.day),
			hour,
			Number(parts.minute),
			Number(parts.second)
		);
		return wallAsUTC - ms;
	}

	/**
	 * Epoch ms of the next midnight in Pacific time after the given instant.
	 *
	 * @param {number} nowMs - Reference epoch milliseconds.
	 * @returns {number} Epoch ms of the upcoming Pacific midnight.
	 */
	function nextPacificMidnightMs(nowMs) {
		// Current Pacific calendar date (YYYY-MM-DD).
		const today = new Intl.DateTimeFormat('en-CA', {
			timeZone: TZ,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit'
		}).format(new Date(nowMs));
		const [y, m, d] = today.split('-').map(Number);

		// Tomorrow 00:00 Pacific, expressed first as if it were UTC...
		const tomorrowWallAsUTC = Date.UTC(y, m - 1, d) + DAY_MS;

		// ...then shifted by the real Pacific offset to get the true epoch.
		let offset = pacificOffsetMs(nowMs);
		let guess = tomorrowWallAsUTC - offset;

		// Refine in the rare case the offset differs at the target (DST change).
		const offsetAtGuess = pacificOffsetMs(guess);
		if (offsetAtGuess !== offset) {
			guess = tomorrowWallAsUTC - offsetAtGuess;
		}
		return guess;
	}

	// Lead sentence shown in the toast; the countdown (component or text) is
	// appended after it.
	const LEAD_TEXT = 'Your page data is out of date! Please refresh now, or the page will refresh in';

	/**
	 * Plain-text fallback message used when the cusa-countdown-timer component
	 * isn't available — the seconds value is updated each tick.
	 *
	 * @param {number} secs - Whole seconds left on the countdown.
	 * @returns {string} The user-facing message.
	 */
	function buildMessage(secs) {
		const unit = secs === 1 ? 'second' : 'seconds';
		return `${LEAD_TEXT} ${secs} ${unit}.`;
	}

	/**
	 * Embeds the shared cusa-countdown-timer web component into the toast message
	 * for a live MM:SS display.
	 *
	 * @param {HTMLElement} msgEl - The toast's message element.
	 * @param {number} countdownEndMs - Absolute epoch ms the countdown ends at.
	 * @returns {boolean} True if the component was embedded; false if unavailable.
	 */
	function renderCountdownTimer(msgEl, countdownEndMs) {
		if (!msgEl) return false;
		if (!(window.customElements && customElements.get('cusa-countdown-timer'))) return false;

		// The toast applies `white-space: pre-line`; our composed content
		// includes the countdown component's own template whitespace, so switch
		// to normal collapsing to keep it on one flowing line.
		msgEl.style.whiteSpace = 'normal';
		msgEl.textContent = `${LEAD_TEXT} `;
		const timer = document.createElement('cusa-countdown-timer');
		timer.dataset.timerId = 'midnight-page-reload';
		// The component scales sub-threshold values from seconds to ms, so pass
		// the end time in seconds.
		timer.dataset.endTime = String(Math.floor(countdownEndMs / 1000));
		timer.dataset.config = JSON.stringify({
			show: ['minutes', 'seconds'],
			order: ['minutes', 'seconds'],
			labelStyle: 'delimiter',
			separator: ':',
			leadingZeros: true
		});
		timer.setAttribute('role', 'timer');
		timer.setAttribute('aria-live', 'polite');
		msgEl.appendChild(timer);
		// No trailing period: the component's template emits trailing whitespace
		// that would render as a gap before the punctuation.
		return true;
	}

	/**
	 * Appends a "Refresh now" button to the toast that reloads immediately.
	 *
	 * @param {HTMLElement} toast - The toast custom element.
	 */
	function addRefreshButton(toast) {
		const body = toast ? toast.querySelector('.guardian-toast-body') : null;
		if (!body) return;
		const btn = document.createElement('button');
		btn.type = 'button';
		// Use the theme's shared `button` class for consistent styling; the
		// marker class only handles layout (alignment/spacing) in the CSS.
		btn.className = 'button cusa-midnight-reload-btn';
		btn.textContent = 'Refresh now';
		btn.addEventListener('click', () => window.location.reload());
		body.appendChild(btn);
	}

	const loadMs = Date.now();
	const refreshAt24h = loadMs + DAY_MS;

	// Default to the 24h fallback; prefer the next Pacific midnight when valid.
	let triggerAt = refreshAt24h;
	try {
		const midnight = nextPacificMidnightMs(loadMs);
		if (Number.isFinite(midnight) && midnight > loadMs) {
			triggerAt = Math.min(midnight, refreshAt24h);
		}
	} catch (err) {
		// Keep the 24h fallback if the timezone math throws for any reason.
		console.warn('[midnight-page-reload] Pacific midnight calc failed; using 24h fallback.', err);
	}

	let checkTimer = null;
	let countdownTimer = null;
	let countdownStarted = false;

	/**
	 * Shows the warning toast and runs the 60-second countdown, then reloads.
	 * Idempotent — safe to call more than once (e.g. from QA tooling).
	 */
	function startCountdown() {
		if (countdownStarted) return;
		countdownStarted = true;

		if (checkTimer) {
			clearInterval(checkTimer);
			checkTimer = null;
		}

		const countdownEnd = Date.now() + COUNTDOWN_SECONDS * 1000;

		let msgEl = null;
		let useComponent = false;
		if (window.CusaToastNotification && typeof window.CusaToastNotification.create === 'function') {
			const toast = window.CusaToastNotification.create({
				message: buildMessage(COUNTDOWN_SECONDS),
				type: 'warning',
				duration: 'infinite'
			});
			if (toast) {
				// Marker class scopes the placement override in
				// cusa-midnight-page-reload.css so this toast clears a page's
				// footer anchor ad (e.g. the Raptive footer on the CTG homepage)
				// without affecting any other toast.
				toast.classList.add('cusa-midnight-reload-toast');
				msgEl = toast.querySelector('.guardian-toast-msg');
				useComponent = renderCountdownTimer(msgEl, countdownEnd);
				addRefreshButton(toast);
			}
		}

		// When the component renders the visible MM:SS it owns the display; this
		// interval owns the reload (and the plain-text fallback when the
		// component isn't available). Comparing against an absolute timestamp
		// keeps the reload correct through tab-throttling and device sleep.
		const tick = () => {
			const remaining = Math.max(0, Math.ceil((countdownEnd - Date.now()) / 1000));
			if (!useComponent && msgEl) msgEl.textContent = buildMessage(remaining);
			if (remaining <= 0) {
				clearInterval(countdownTimer);
				countdownTimer = null;
				window.location.reload();
			}
		};
		tick();
		countdownTimer = setInterval(tick, 1000);
	}

	// Poll for the trigger. An absolute-timestamp compare means a throttled or
	// slept tab still fires correctly the moment it next ticks.
	checkTimer = setInterval(() => {
		if (Date.now() >= triggerAt) {
			startCountdown();
		}
	}, CHECK_INTERVAL_MS);

	// Exposed for QA / Playwright verification.
	window.CusaMidnightPageReload = {
		startCountdown,
		getState: () => ({
			loadMs,
			triggerAt,
			refreshAt24h,
			msUntilTrigger: triggerAt - Date.now(),
			countdownStarted
		})
	};
})();
